import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Clickable from 'components/Common/Clickable'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import FlightDealModal from './FlightDealModal'
import OfferCalendarFlightDealBannerCountdown from '../Filters/CalendarPicker/OfferCalendarFlightDealBannerCountdown'
import { shouldShowFlightDealAvailabilityCta } from './flightDealDateUtils'

interface Props {
  activeDeal: App.FlightDeal
  showCountdown?: boolean
}

function FlightDealLabel(props: Props) {
  const { activeDeal, showCountdown = true } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const offerPageState = useContext(OfferPageStateContext)
  const dispatch = useOfferPageDispatch()

  const isOfferPage = !!(offerPageState.offer || offerPageState.bedbankOffer || offerPageState.property || offerPageState.tourV2Offer)

  const showCheckAvailabilityCta = useMemo(() => shouldShowFlightDealAvailabilityCta({
    isOfferPage,
    checkInDate: offerPageState.checkInDate,
    checkOutDate: offerPageState.checkOutDate,
    duration: offerPageState.duration,
    travelPeriods: activeDeal.travelPeriods,
  }), [activeDeal.travelPeriods, isOfferPage, offerPageState.checkInDate, offerPageState.checkOutDate, offerPageState.duration])

  const handleCheckAvailability = useCallback(() => {
    dispatch({ type: OfferPageActions.checkAvailability, filter: 'checkin', flights: true })
  }, [dispatch])

  const handleOpenFlightDealModal = useCallback(() => {
    showModal(
      <FlightDealModal
        activeDeal={activeDeal}
        showCheckAvailabilityCta={showCheckAvailabilityCta}
        onCheckAvailability={handleCheckAvailability}
      />,
    )
  }, [activeDeal, handleCheckAvailability, showCheckAvailabilityCta, showModal])

  const flightSaleEndsInMessage = intl.formatMessage(
    {
      id: 'K9la6FQKwEB8UtxfYSrA',
      defaultMessage: 'Flight sale ends in {countdown}',
      description: 'Flight deal urgency label with countdown on offer page banner',
    },
    {
      countdown: <OfferCalendarFlightDealBannerCountdown salesEndDate={activeDeal.salesEndDate} />,
    },
  )

  const flightSaleMessage = intl.formatMessage({
    id: '1ZxZrOONDKHC8Z3PLAkR',
    defaultMessage: 'Flight sale',
    description: 'Flight deal urgency label on offer page banner when countdown is hidden',
  })

  return (
    <Clickable onClick={handleOpenFlightDealModal}>
      <UrgencyLabel
        icon={<SolidFireIcon />}
        variant="critical2"
        endIcon={<LineInfoCircleIcon />}
      >
        {showCountdown ? flightSaleEndsInMessage : flightSaleMessage}
      </UrgencyLabel>
    </Clickable>
  )
}

export default FlightDealLabel
